/**
 * OnePayment - Drop-in Payment Element (Web Component)
 * @element one-payment
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type {
  Adapters,
  SDKConfig,
  State,
  PaymentMethodId,
  PaymentDataResult,
  FeeConfig,
} from '@one-payments/core';
import { PaymentSDK, PAYMENT_METHODS, PAYNOW_ICON_DATA_URL } from '@one-payments/core';
import type Cleave from 'cleave.js';
import CleaveConstructor from 'cleave.js';
import QRCode from 'qrcode';


/**
 * Custom events emitted by this component
 */
export interface OnePaymentEvents {
  'payment-success': CustomEvent<{ paymentIntentId: string }>;
  'payment-error': CustomEvent<{ error: string }>;
  'state-change': CustomEvent<{ state: State }>;
}

/**
 * Form data interfaces
 */
interface CardFormData {
  cardNumber: string;
  expiry: string;
  cvv: string;
  cardholderName: string;
}

/**
 * Card brand type
 */
type CardBrand = 'visa' | 'mastercard' | 'amex' | 'discover' | 'unknown';

/**
 * Main payment component - framework-agnostic web component with EXACT original design
 *
 * OnePayment is a drop-in payment element that handles the complete payment flow including
 * card payments, PayNow QR codes, 3D Secure authentication, and real-time status updates.
 *
 * @element one-payment
 *
 * @example
 * ```typescript
 * import '@one-payments/web-components';
 * import { PaymentConfig } from '@one-payments/core';
 * import { createWebAdapters } from '@one-payments/adapters';
 *
 * const config = new PaymentConfig({
 *   apiKey: 'pk_test_...',
 *   secretKey: 'sk_test_...',
 *   environment: 'demo'
 * });
 *
 * <one-payment
 *   .config=${config}
 *   .adapters=${createWebAdapters()}
 *   amount=${10000}
 *   currency="SGD"
 *   orderId="order_123"
 *   firstName="John"
 *   lastName="Doe"
 *   email="john@example.com"
 * ></one-payment>
 * ```
 *
 * @fires payment-success - Emitted when payment succeeds, contains paymentIntentId
 * @fires payment-error - Emitted when payment fails, contains error message
 * @fires state-change - Emitted on every state change, contains full state object
 *
 * @public
 * @since 1.0.0
 */
@customElement('one-payment')
export class OnePayment extends LitElement {
  // --- Public Props ---

  /**
   * SDK configuration object
   *
   * **RECOMMENDED**: Use the `PaymentConfig` class to create this configuration:
   * ```typescript
   * import { PaymentConfig } from '@one-payments/core';
   *
   * const config = new PaymentConfig({
   *   apiKey: 'pk_test_...',
   *   secretKey: 'sk_test_...',
   *   environment: 'demo'
   * });
   * ```
   *
   * The config can also be a plain object:
   * ```typescript
   * const config = {
   *   apiKey: 'pk_test_...',
   *   secretKey: 'sk_test_...',
   *   environment: 'demo'
   * };
   * ```
   *
   * **Required fields:**
   * - `apiKey` - Your One Payments public API key
   * - `secretKey` - Your One Payments secret key
   * - `environment` - 'prod', 'demo', or 'dev'
   *
   * **Optional fields:**
   * - `apiBaseUrl` - Custom API endpoint (defaults based on environment)
   * - `locale` - Language/locale code (e.g., 'en-US', 'zh-CN')
   *
   * @required
   * @type {SDKConfig | PaymentConfig}
   * @public
   */
  @property({ type: Object }) config!: SDKConfig;

  /**
   * Platform-specific adapters for HTTP and storage operations
   *
   * Adapters abstract platform differences (web, React Native, Node.js) so the
   * core SDK can work across all environments.
   *
   * **For web/browser environments:**
   * ```typescript
   * import { createWebAdapters } from '@one-payments/adapters';
   *
   * <one-payment
   *   .adapters=${createWebAdapters()}
   *   ...
   * />
   * ```
   *
   * **For React Native:**
   * ```typescript
   * import { createReactNativeAdapters } from '@one-payments/adapters';
   *
   * <one-payment
   *   .adapters=${createReactNativeAdapters()}
   *   ...
   * />
   * ```
   *
   * @required
   * @type {Adapters}
   * @public
   */
  @property({ type: Object }) adapters!: Adapters;

  /**
   * Payment amount in smallest currency unit (cents/pence)
   *
   * **IMPORTANT**: Always provide amounts in the smallest currency unit:
   * - For USD/SGD/EUR: cents (e.g., 10000 = $100.00)
   * - For JPY/KRW: yen/won (e.g., 1000 = ¥1000)
   *
   * Examples:
   * - $10.00 USD = 1000 cents
   * - €25.50 EUR = 2550 cents
   * - ¥5000 JPY = 5000 yen
   *
   * The component will automatically format the display based on currency.
   *
   * @required
   * @type {number}
   * @public
   */
  @property({ type: Number }) amount!: number;

  /**
   * Three-letter ISO 4217 currency code
   *
   * Specifies the currency for the payment transaction. Must be a valid
   * ISO 4217 currency code (e.g., 'USD', 'EUR', 'SGD', 'GBP').
   *
   * **Important currency-specific behavior:**
   * - PayNow is ONLY available for SGD (Singapore Dollars)
   * - Other payment methods work with most major currencies
   *
   * Common currency codes:
   * - USD - US Dollar
   * - EUR - Euro
   * - GBP - British Pound
   * - SGD - Singapore Dollar
   * - JPY - Japanese Yen
   * - AUD - Australian Dollar
   * - CAD - Canadian Dollar
   *
   * @required
   * @type {string}
   * @public
   */
  @property({ type: String }) currency!: string;

  /**
   * Unique order/transaction identifier
   *
   * A unique identifier for this payment transaction from your system.
   * This ID is used to:
   * - Track the payment in your backend
   * - Prevent duplicate payments
   * - Reference the transaction in webhooks and API calls
   *
   * **Best practices:**
   * - Use a unique, non-sequential ID (e.g., UUID)
   * - Store this ID in your database before initiating payment
   * - Use the same format consistently across your system
   *
   * Example formats:
   * - `"order_abc123xyz"`
   * - `"txn_1234567890"`
   * - `"550e8400-e29b-41d4-a716-446655440000"` (UUID)
   *
   * @required
   * @type {string}
   * @public
   */
  @property({ type: String }) orderId!: string;

  /**
   * Customer's first name (REQUIRED)
   *
   * The payer's first name or given name. This field is required for all
   * payment methods and is used for:
   * - Payment receipt generation
   * - Transaction records
   * - Fraud prevention and verification
   *
   * **Validation:**
   * - Cannot be empty or whitespace-only
   * - Must be provided before payment submission
   *
   * @required
   * @type {string}
   * @public
   */
  @property({ type: String }) firstName!: string;

  /**
   * Customer's last name (REQUIRED)
   *
   * The payer's last name or family name. This field is required for all
   * payment methods and is used for:
   * - Payment receipt generation
   * - Transaction records
   * - Fraud prevention and verification
   *
   * **Validation:**
   * - Cannot be empty or whitespace-only
   * - Must be provided before payment submission
   *
   * @required
   * @type {string}
   * @public
   */
  @property({ type: String }) lastName!: string;

  /**
   * Customer's email address (REQUIRED)
   *
   * The payer's email address. This field is required for all payment methods
   * and is used for:
   * - Sending payment receipts and confirmations
   * - Transaction notifications
   * - Customer communication
   * - Fraud prevention
   *
   * **Validation:**
   * - Cannot be empty or whitespace-only
   * - Must be provided before payment submission
   * - Should be a valid email format (validated by your application)
   *
   * @required
   * @type {string}
   * @public
   */
  @property({ type: String }) email!: string;

  /**
   * Optional array of payment method IDs to exclude from display
   *
   * Use this to hide specific payment methods that you don't want to offer
   * for this transaction. The component will only show methods that are:
   * 1. Available from the API
   * 2. Not in this exclusion list
   * 3. Compatible with the transaction currency
   *
   * Available payment method IDs:
   * - `PAYMENT_METHODS.CARD` - Credit/debit cards
   * - `PAYMENT_METHODS.PAYNOW` - PayNow QR code (SGD only)
   *
   * Example:
   * ```typescript
   * import { PAYMENT_METHODS } from '@one-payments/core';
   *
   * // Hide PayNow, only show cards
   * <one-payment
   *   .excludePaymentMethods=${[PAYMENT_METHODS.PAYNOW]}
   *   ...
   * />
   * ```
   *
   * @optional
   * @type {PaymentMethodId[]}
   * @default undefined
   * @public
   */
  @property({ type: Array }) excludePaymentMethods?: PaymentMethodId[];

  /**
   * Optional custom width for the payment component
   *
   * Sets the width of the entire payment element. Can be any valid CSS width value.
   *
   * **Examples:**
   * - `"500px"` - Fixed pixel width
   * - `"100%"` - Full width of container
   * - `"50vw"` - 50% of viewport width
   * - `"auto"` - Automatic width based on content
   *
   * **Default:** `"100%"` (full width of container)
   *
   * **Note:** The component has a minimum width of 320px for proper mobile display.
   *
   * @optional
   * @type {string}
   * @default "100%"
   * @public
   */
  @property({ type: String }) width?: string;

  /**
   * Optional maximum width constraint for the payment component
   *
   * Sets the maximum width the payment element can grow to. Useful for
   * preventing the component from becoming too wide on large screens.
   *
   * **Examples:**
   * - `"600px"` - Max width of 600 pixels
   * - `"90%"` - Max 90% of container width
   * - `"1200px"` - Max width for large displays
   *
   * **Default:** `none` (no maximum width constraint)
   *
   * **Tip:** Combine with `width="100%"` for responsive layouts that don't
   * exceed a certain size:
   * ```typescript
   * <one-payment
   *   width="100%"
   *   maxWidth="600px"
   *   ...
   * />
   * ```
   *
   * @optional
   * @type {string}
   * @default "none"
   * @public
   */
  @property({ type: String }) maxWidth?: string;

  // --- Internal State ---
  @state() private currentState: State = { status: 'idle' };
  @state() private selectedMethod: PaymentMethodId | null = null;
  @state() private cardFormData: CardFormData = {
    cardNumber: '',
    expiry: '',
    cvv: '',
    cardholderName: '',
  };
  @state() private formErrors: Partial<Record<keyof CardFormData, string>> = {};
  @state() private paymentData: PaymentDataResult | null = null;
  @state() private feesExpanded = false;
  @state() private isInitializing = true;
  @state() private cardBrand: CardBrand = 'unknown';

  // New state for 3DS and QR flows
  @state() private paymentLocked = false;
  @state() private show3DSModal = false;
  @state() private showQRModal = false;
  @state() private nextActionUrl: string | null = null;
  @state() private qrCode: string | null = null;
  @state() private qrCodeDataUrl: string | null = null; // Generated QR image
  @state() private qrAutoResumeTimer: number | null = null;
  @state() private qrPollingInProgress = false; // Track if QR polling is in progress

  private sdk: PaymentSDK | null = null;
  private isInitialized = false;
  private cardNumberCleave: Cleave | null = null;
  private expiryCleave: Cleave | null = null;
  private leaveEventsCounter = 0;
  private previousState: State | null = null;

  // --- Styles (EXACT original design from PaymentElement.module.scss) ---
  static override styles = css`
    :host {
      display: block;
      font-family:
        -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      box-sizing: border-box;
    }

    * {
      box-sizing: border-box;
    }

    /* Payment Element Container */
    .payment-element {
      width: var(--one-payment-width, 100%);
      max-width: var(--one-payment-max-width, none);
      min-width: 320px;
    }

    .header {
      text-align: left;
      padding: 1rem 0;
      color: #6b7280;
      font-size: 0.875rem;
      font-weight: 400;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 1rem;
    }

    .payment-methods-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-top: 1rem;
      width: 100%;
    }

    .payment-method-item {
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      overflow: visible;
      background: #ffffff;
      width: 100%;
    }

    .method-header {
      display: flex;
      align-items: center;
      padding: 1rem 1.25rem;
      background: #ffffff;
      cursor: pointer;
      transition: background-color 150ms ease;
      user-select: none;
      width: 100%;
      border-radius: 0.5rem 0.5rem 0 0;
    }

    .method-header:hover {
      background-color: #f9fafb;
    }

    .method-header.selected {
      background-color: #f3f4f6;
    }

    .radio {
      width: 1.25rem;
      height: 1.25rem;
      min-width: 1.25rem;
      margin: 0;
      margin-right: 10px;
      flex-shrink: 0;
      cursor: pointer;
      accent-color: #ffbe32;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      border: 2px solid #d1d5db;
      border-radius: 50%;
      background-color: #ffffff;
      position: relative;
      transition: border-color 150ms ease;
    }

    .radio:checked {
      border-color: #ffbe32;
      background-color: #ffffff;
    }

    .radio:checked::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0.625rem;
      height: 0.625rem;
      border-radius: 50%;
      background-color: #ffbe32;
    }

    .radio:hover:not(:disabled) {
      border-color: #9ca3af;
    }

    .method-icon {
      width: 2rem;
      height: 2rem;
      min-width: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.25rem;
      flex-shrink: 0;
      color: #6b7280;
      margin-right: 0.625rem;
    }

    .method-icon-right {
      width: 1.5rem;
      height: 1.5rem;
      min-width: 1.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: #6b7280;
    }

    .method-icon-right svg {
      width: 100%;
      height: 100%;
    }

    .method-icon-right img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .method-name {
      font-size: 1rem;
      font-weight: 500;
      color: #111827;
      flex-shrink: 0;
      text-align: left;
      margin-right: 10px;
    }

    /* Accordion Content */
    .method-content {
      padding: 1.5rem;
      background-color: #ffffff;
      border-top: 1px solid #e5e7eb;
      width: 100%;
      overflow: visible;
    }

    /* Card Form */
    .card-form {
      width: 100%;
      max-width: 100%;
    }

    .form-title {
      font-size: 1rem;
      font-weight: 500;
      color: #111827;
      margin: 0 0 1rem 0;
      text-align: left;
    }

    .form-fields {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      width: 100%;
    }

    .form-group {
      width: 100%;
    }

    .form-label {
      display: block;
      margin-bottom: 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: #374151;
      text-align: left;
    }

    .input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid #d1d5db;
      border-radius: 0.375rem;
      font-size: 0.9375rem;
      line-height: 1.25rem;
      background: white;
      color: black;
      transition: all 150ms ease;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }

    .input:focus {
      outline: none;
      border-color: #7c3aed;
      box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
    }

    .input::placeholder {
      color: #9ca3af;
    }

    .input:hover:not(:focus) {
      border-color: #9ca3af;
    }

    .input.input-error {
      border-color: #dc2626;
    }

    .input.input-error:focus {
      border-color: #dc2626;
      box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
    }

    .expiry-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
      width: 100%;
    }

    .error-message {
      margin-top: 0.25rem;
      color: #dc2626;
      font-size: 0.75rem;
      line-height: 1.25;
    }

    /* Submit Section */
    .submit-section {
      margin-top: 1.5rem;
      width: 100%;
    }

    /* Pay Button - Modern Design */
    .pay-button {
      display: inline-block;
      width: 100%;
      height: 52px;
      padding: 10px 24px;
      font-size: 18px;
      font-weight: 650;
      color: #ffbe32;
      line-height: normal;
      border: 0;
      border-radius: 40px;
      text-decoration: none;
      transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        color 0.2s ease,
        opacity 0.2s ease;
      overflow: hidden;
      user-select: none;
      position: relative;
      cursor: pointer;
      background-color: #2a2327;
      white-space: nowrap;
      letter-spacing: 0;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }

    .pay-button:disabled {
      cursor: not-allowed;
      color: rgba(255, 190, 50, 0.8);
      background-color: rgba(42, 35, 39, 0.8);
    }

    .pay-button:not(:disabled):hover,
    .pay-button:not(:disabled):focus-visible {
      background-color: #000;
    }

    .pay-button:not(:disabled):active {
      transform: translateY(1px);
    }

    /* Button Loading Spinner */
    .pay-button.loading,
    .paynow-container .scanned-button.loading {
      position: relative;
      color: transparent !important;
      pointer-events: none;
    }

    .pay-button.loading::after,
    .paynow-container .scanned-button.loading::after {
      content: '';
      position: absolute;
      width: 20px;
      height: 20px;
      top: 50%;
      left: 50%;
      margin-left: -10px;
      margin-top: -10px;
      border: 2px solid transparent;
      border-top-color: #ffbe32;
      border-radius: 50%;
      animation: button-spinner 0.8s linear infinite;
    }

    @keyframes button-spinner {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    /* Loading State */
    .processing-state {
      text-align: center;
      padding: 2rem 0;
    }

    .loader {
      width: 40px;
      height: 40px;
      border: 3px solid #f3f4f6;
      border-top: 3px solid #3b82f6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 1rem;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    .processing-text {
      color: #6b7280;
      margin: 0;
    }

    /* PayNow */
    .paynow-container {
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .paynow-container .scanned-button {
      display: inline-block;
      width: 100%;
      height: 52px;
      padding: 10px 24px;
      font-size: 18px;
      font-weight: 650;
      color: #ffbe32;
      line-height: normal;
      border: 0;
      border-radius: 40px;
      text-decoration: none;
      transition:
        background-color 0.2s ease,
        transform 0.05s ease;
      overflow: hidden;
      user-select: none;
      position: relative;
      cursor: pointer;
      background-color: #2a2327;
      white-space: nowrap;
      letter-spacing: 0;
    }

    .paynow-container .scanned-button:disabled {
      cursor: not-allowed;
      color: rgba(255, 190, 50, 0.8);
      background-color: rgba(42, 35, 39, 0.8);
    }

    .paynow-container .scanned-button:not(:disabled):hover,
    .paynow-container .scanned-button:not(:disabled):focus-visible {
      background-color: #000;
    }

    .paynow-container .scanned-button:not(:disabled):active {
      transform: translateY(1px);
    }

    /* PayNow Instructions */
    .paynow-instructions {
      padding: 0;
      width: 100%;
    }

    .instructions-title {
      font-size: 1rem;
      font-weight: 600;
      color: #111827;
      margin: 0 0 1.5rem 0;
      text-align: left;
    }

    .instruction-steps {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 1.5rem;
    }

    .instruction-step {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      padding: 0.5rem 0;
    }

    .step-number {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.75rem;
      height: 1.75rem;
      min-width: 1.75rem;
      border-radius: 50%;
      background-color: #ffbe32;
      color: #ffffff;
      font-size: 0.875rem;
      font-weight: 600;
      flex-shrink: 0;
    }

    .step-text {
      flex: 1;
      margin: 0;
      padding-top: 0.125rem;
      color: #374151;
      font-size: 0.9375rem;
      line-height: 1.5;
    }

    .qr-code-section {
      text-align: center;
      padding: 1rem 0;
    }

    .qr-instruction {
      color: #6b7280;
      font-size: 14px;
      margin-bottom: 1.5rem;
      line-height: 1.5;
    }

    .qr-code-wrapper {
      display: flex;
      justify-content: center;
      margin-bottom: 1.5rem;
    }

    .qr-canvas {
      border: 4px solid #f3f4f6;
      border-radius: 12px;
      padding: 16px;
      background: white;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
      width: 200px;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #9ca3af;
    }

    .scanned-button {
      padding: 12px 24px;
      background: #10b981;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .scanned-button:hover:not(:disabled) {
      background: #059669;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
    }

    .scanned-button:active {
      transform: translateY(0);
    }

    .scanned-button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }

    /* Result Status Block - Replaces payment methods when succeeded/failed */
    .result-card {
      background: white;
      border-radius: 12px;
      padding: 40px;
      width: 100%;
      text-align: center;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
      border: 2px solid transparent;
      transition: all 0.3s ease;
      margin-top: 0;
    }

    .result-card.success {
      border-color: #10b981;
      background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%);
    }

    .result-card.error {
      border-color: #ef4444;
      background: linear-gradient(135deg, #fef2f2 0%, #ffffff 100%);
    }

    .result-icon-wrapper {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 24px;
      animation: scaleIn 0.5s ease-out;
    }

    .result-card.success .result-icon-wrapper {
      background: #10b981;
    }

    .result-card.error .result-icon-wrapper {
      background: #ef4444;
    }

    @keyframes scaleIn {
      from {
        transform: scale(0);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }

    .result-icon {
      font-size: 40px;
      color: white;
      font-weight: bold;
    }

    .result-title {
      margin: 0 0 12px 0;
      font-size: 28px;
      font-weight: 700;
      color: #111827;
    }

    .result-message {
      margin: 0 0 32px 0;
      font-size: 16px;
      color: #6b7280;
      line-height: 1.5;
    }

    .retry-button {
      display: inline-block;
      min-width: 162px;
      height: 52px;
      padding: 10px 24px;
      font-size: 18px;
      font-weight: 650;
      color: #ffbe32;
      line-height: normal;
      border: 0;
      border-radius: 40px;
      text-decoration: none;
      transition:
        background-color 0.2s ease,
        transform 0.05s ease;
      overflow: hidden;
      user-select: none;
      position: relative;
      cursor: pointer;
      background-color: #2a2327;
      white-space: nowrap;
      letter-spacing: 0;
    }

    .retry-button:hover {
      background: #000;
    }

    .retry-button:active {
      transform: translateY(1px);
    }

    /* Mobile responsiveness */
    @media (max-width: 640px) {
      .header {
        padding: 0.75rem 0;
        font-size: 0.8125rem;
      }

      .payment-methods-list {
        gap: 0.75rem;
      }

      .method-header {
        padding: 0.875rem 1rem;
      }

      .radio {
        width: 1.125rem;
        height: 1.125rem;
        min-width: 1.125rem;
      }

      .method-icon {
        width: 1.75rem;
        height: 1.75rem;
        min-width: 1.75rem;
        margin-right: 0.625rem;
        font-size: 1.125rem;
      }

      .method-name {
        font-size: 0.9375rem;
      }

      .method-content {
        padding: 1.25rem 1rem;
      }

      .form-title {
        font-size: 0.9375rem;
        margin-bottom: 0.875rem;
      }

      .form-fields {
        gap: 0.875rem;
      }

      .input {
        padding: 0.625rem 0.75rem;
        font-size: 16px;
      }

      .expiry-row {
        gap: 0.75rem;
      }

      .form-label {
        font-size: 0.8125rem;
        margin-bottom: 0.375rem;
      }

      .submit-section {
        margin-top: 1.25rem;
      }

      .pay-button {
        padding: 0.75rem;
        font-size: 1rem;
        border-radius: 0.375rem;
      }

      .result-card {
        padding: 24px;
      }

      .paynow-instructions {
        padding: 0;
      }

      .instructions-title {
        font-size: 0.9375rem;
        margin-bottom: 1.25rem;
      }

      .instruction-steps {
        gap: 0.875rem;
        margin-bottom: 1.25rem;
      }

      .instruction-step {
        gap: 0.625rem;
        padding: 0.375rem 0;
      }

      .step-number {
        width: 1.5rem;
        height: 1.5rem;
        min-width: 1.5rem;
        font-size: 0.8125rem;
      }

      .step-text {
        font-size: 0.875rem;
      }
    }

    /* Transaction Details */
    .transaction-details {
      padding: 1.25rem;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      margin-bottom: 1rem;
    }

    .transaction-title {
      font-size: 1.125rem;
      font-weight: 600;
      color: #111827;
      margin: 0 0 1rem 0;
      line-height: 1.2;
    }

    .details-grid {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .details-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .details-label {
      font-size: 0.875rem;
      font-weight: 500;
      color: #6b7280;
    }

    .details-value {
      font-size: 0.875rem;
      font-weight: 600;
      color: #111827;
      text-align: right;
    }

    .fees-toggle {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      font-family: inherit;
    }

    .fees-toggle:hover {
      opacity: 0.8;
    }

    .fee-breakdown {
      width: 100%;
      display: flex;
      position: relative;
      padding-left: 1.75rem;
      overflow: hidden;
      transition:
        max-height 0.3s ease-out,
        opacity 0.3s ease-out,
        margin-top 0.3s ease-out;
    }

    .fee-breakdown::before {
      content: '';
      position: absolute;
      left: 0.9375rem;
      top: 0.625rem;
      bottom: 0.625rem;
      border-left: 2px solid #d1d5db;
    }

    .fee-breakdown-inner {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0.625rem;
      padding-left: 1rem;
      padding-top: 0.625rem;
    }

    .breakdown-row {
      width: 100%;
      position: relative;
      padding: 0.5rem 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .breakdown-row:first-child::before {
      content: '';
      position: absolute;
      left: -2.0625rem;
      top: 50%;
      transform: translateY(-50%);
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #d1d5db;
    }

    .breakdown-row:first-child::after {
      content: '';
      position: absolute;
      left: -1.5rem;
      top: 50%;
      transform: translateY(-50%);
      width: 1rem;
      height: 2px;
      background: #d1d5db;
    }

    .breakdown-row:nth-child(2) {
      padding-left: 0.5rem;
      padding-bottom: 0;
    }

    .breakdown-row:nth-child(2)::before {
      content: '';
      position: absolute;
      left: -1.8275rem;
      top: 60%;
      transform: translateY(-60%);
      width: 1.5rem;
      height: 2px;
      background: #d1d5db;
    }

    .breakdown-row:nth-child(2)::after {
      content: '';
      position: absolute;
      left: -0.3rem;
      top: calc(60% - 0.25rem);
      border-top: 4px solid transparent;
      border-bottom: 4px solid transparent;
      border-left: 6px solid #d1d5db;
    }

    .breakdown-label {
      font-size: 0.875rem;
      font-weight: 400;
      color: #6b7280;
    }

    .breakdown-value {
      font-size: 0.875rem;
      font-weight: 500;
      color: #111827;
      text-align: right;
    }

    .transaction-divider {
      height: 1px;
      background: #d1d5db;
      margin: 1rem 0;
    }

    .net-amount-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .net-label {
      font-size: 1rem;
      font-weight: 600;
      color: #111827;
    }

    .net-value {
      font-size: 1.25rem;
      font-weight: 700;
      color: #111827;
      text-align: right;
    }

    /* Skeleton Loading */
    .skeleton {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 0.25rem;
    }

    @keyframes shimmer {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }

    .skeleton-transaction {
      padding: 1.25rem;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      margin-bottom: 1rem;
    }

    .skeleton-title {
      height: 1.5rem;
      width: 60%;
      margin-bottom: 1rem;
    }

    .skeleton-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.75rem;
    }

    .skeleton-label {
      height: 1rem;
      width: 30%;
    }

    .skeleton-value {
      height: 1rem;
      width: 25%;
    }

    .skeleton-divider {
      height: 1px;
      background: #d1d5db;
      margin: 1rem 0;
    }

    .skeleton-net-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .skeleton-net-label {
      height: 1.25rem;
      width: 35%;
    }

    .skeleton-net-value {
      height: 1.5rem;
      width: 30%;
    }

    .skeleton-method {
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      padding: 1rem 1.25rem;
      background: #ffffff;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .skeleton-radio {
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .skeleton-icon {
      width: 2rem;
      height: 2rem;
      border-radius: 0.25rem;
      flex-shrink: 0;
    }

    .skeleton-method-name {
      height: 1.25rem;
      width: 40%;
    }

    /* 3DS Modal */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      padding: 1rem;
    }

    .modal-content {
      position: relative;
      width: 100%;
      max-width: 500px;
      max-height: 90vh;
      background: white;
      border-radius: 8px;
      overflow-y: auto;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    }

    .modal-content.iframe-modal {
      height: 600px;
      overflow: hidden;
    }

    .modal-iframe {
      width: 100%;
      height: 100%;
      border: none;
    }

    .modal-content .qr-code-section {
      padding: 2rem;
      min-height: 500px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: 1.5rem;
    }

    .modal-content .qr-code-section .submit-section {
      width: 100%;
      max-width: 300px;
      margin-top: auto;
      padding-top: 2rem;
    }

    .modal-content .qr-code-section .scanned-button {
      width: 100%;
      padding: 16px 24px;
      background: #10b981;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 18px;
      font-weight: 700;
      cursor: pointer;
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    /* QR Modal with iframe */
    .qr-modal-content {
      display: flex;
      flex-direction: column;
      height: 650px;
      max-height: 85vh;
      padding: 0;
      overflow: hidden;
      background: linear-gradient(to bottom, #ffffff 0%, #f9fafb 100%);
    }

    .qr-iframe-container {
      flex: 1;
      width: 100%;
      overflow: hidden;
      background: white;
      position: relative;
    }

    .qr-iframe {
      width: 100%;
      height: 100%;
      border: none;
      background: white;
    }

    .qr-button-container {
      padding: 2rem 2.5rem;
      background: linear-gradient(to top, #ffffff 0%, #fafbfc 100%);
      border-top: 1px solid rgba(0, 0, 0, 0.08);
      display: flex;
      flex-direction: column;
      gap: 1rem;
      justify-content: center;
      align-items: center;
      min-height: 140px;
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.03);
    }

    .qr-button-container::before {
      content: '';
      position: absolute;
      top: -1px;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(16, 185, 129, 0.3) 50%,
        transparent 100%
      );
    }

    .qr-button-hint {
      color: #6b7280;
      font-size: 14px;
      font-weight: 500;
      text-align: center;
      margin: 0;
      padding: 0 1rem;
      line-height: 1.5;
    }

    .qr-button-container .scanned-button {
      width: 100%;
      max-width: 420px;
      padding: 20px 40px;
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      color: white;
      border: none;
      border-radius: 16px;
      font-size: 17px;
      font-weight: 700;
      cursor: pointer;
      box-shadow:
        0 8px 24px rgba(16, 185, 129, 0.35),
        0 2px 8px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
      text-transform: none;
      letter-spacing: 0.3px;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
    }

    .qr-button-container .scanned-button::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s;
    }

    .qr-button-container .scanned-button:hover::before {
      left: 100%;
    }

    .qr-button-container .scanned-button:hover {
      background: linear-gradient(135deg, #059669 0%, #047857 100%);
      transform: translateY(-3px);
      box-shadow:
        0 12px 32px rgba(16, 185, 129, 0.45),
        0 4px 12px rgba(0, 0, 0, 0.12),
        inset 0 1px 0 rgba(255, 255, 255, 0.25);
    }

    .qr-button-container .scanned-button:active {
      transform: translateY(-1px);
      box-shadow:
        0 6px 20px rgba(16, 185, 129, 0.4),
        0 2px 6px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
    }

    /* Add icon to button */
    .qr-button-container .scanned-button::after {
      content: '✓';
      margin-left: 12px;
      font-size: 20px;
      display: inline-block;
      transition: transform 0.25s ease;
    }

    .qr-button-container .scanned-button:hover::after {
      transform: scale(1.2);
    }

    /* Inline QR Code (not in modal) */
    .qr-iframe-container-inline {
      width: 100%;
      height: 450px;
      border-radius: 8px;
      overflow: hidden;
      background: white;
      border: 1px solid #e5e7eb;
    }

    .qr-iframe-inline {
      width: 100%;
      height: 100%;
      border: none;
      background: white;
    }

    .radio:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .method-header.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .method-header.disabled:hover {
      background-color: #ffffff;
    }

    /* Tooltip for disabled payment methods */
    .payment-method-item {
      position: relative;
    }

    .payment-method-item[data-tooltip]:hover::after {
      content: attr(data-tooltip);
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      margin-top: 0.5rem;
      padding: 0.5rem 0.75rem;
      background: #1f2937;
      color: white;
      font-size: 0.875rem;
      line-height: 1.25rem;
      border-radius: 0.375rem;
      white-space: nowrap;
      z-index: 1000;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      animation: tooltipFadeIn 0.2s ease;
    }

    .payment-method-item[data-tooltip]:hover::before {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      margin-top: 0.25rem;
      border: 5px solid transparent;
      border-bottom-color: #1f2937;
      z-index: 1001;
    }

    @keyframes tooltipFadeIn {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(-5px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }
  `;

  // --- Lifecycle ---
  override connectedCallback(): void {
    super.connectedCallback();

    // Set up postMessage listener for 3DS
    window.addEventListener('message', this.handle3DSPostMessage);

    // React Native Fix: Listen for force close event from payment.html template
    // This is triggered when poll_started fires in React Native WebView
    this.addEventListener('force-close-3ds-modal', () => {
      if (this.show3DSModal) {
        this.show3DSModal = false;
        this.nextActionUrl = null;
        this.leaveEventsCounter = 0;
      }
    });
  }

  override firstUpdated(): void {
    // Initialize SDK after properties are set
    // This runs AFTER the first render and property updates
    this.initializeSDK();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.sdk?.destroy();
    this.isInitialized = false;
    this.destroyCleave();

    // Clean up postMessage listener
    window.removeEventListener('message', this.handle3DSPostMessage);

    // Clean up QR timer if exists
    if (this.qrAutoResumeTimer !== null) {
      clearTimeout(this.qrAutoResumeTimer);
    }
  }

  private handle3DSPostMessage = (event: MessageEvent): void => {
    if (event.data === 'ON_AWX_3DS_START_PAGE_LEAVE') {
      this.leaveEventsCounter++;
      // Only close modal after receiving the SECOND leave event
      if (this.leaveEventsCounter > 1) {
        this.handle3DSComplete();
      }
    }
  };

  // --- Cleave.js Setup ---
  private setupCleave(): void {
    // Delay to ensure DOM is fully rendered
    setTimeout(() => {
      const cardNumberInput = this.shadowRoot?.querySelector<HTMLInputElement>('#cardNumber');
      const expiryInput = this.shadowRoot?.querySelector<HTMLInputElement>('#expiry');

      if (cardNumberInput && !this.cardNumberCleave) {
        this.cardNumberCleave = new CleaveConstructor(cardNumberInput, {
          creditCard: true,
          delimiter: ' ',
          onCreditCardTypeChanged: (type: string) => {
            // Map Cleave card type to our CardBrand
            const brandMap: Record<string, CardBrand> = {
              visa: 'visa',
              mastercard: 'mastercard',
              amex: 'amex',
              discover: 'discover',
            };
            this.cardBrand = brandMap[type] || 'unknown';
          },
        });
      }

      if (expiryInput && !this.expiryCleave) {
        this.expiryCleave = new CleaveConstructor(expiryInput, {
          date: true,
          datePattern: ['m', 'y'],
          delimiter: '/',
        });
      }
    }, 50);
  }

  private destroyCleave(): void {
    if (this.cardNumberCleave) {
      this.cardNumberCleave.destroy();
      this.cardNumberCleave = null;
    }
    if (this.expiryCleave) {
      this.expiryCleave.destroy();
      this.expiryCleave = null;
    }
  }

  // --- Methods ---
  /**
   * Get filtered and validated payment methods
   * Applies three layers of filtering:
   * 1. API availability (from available-methods endpoint)
   * 2. User exclusions (excludePaymentMethods prop)
   * 3. Currency validation (PayNow only for SGD)
   */
  private getFilteredPaymentMethods(): Array<{
    id: PaymentMethodId;
    enabled: boolean;
    disabledReason?: string;
  }> {
    // Get available methods from SDK state (from API)
    // Include 'processing' and 'requires_action' states so methods stay visible during payment
    const availableMethods =
      this.currentState.status === 'ready' ||
      this.currentState.status === 'processing' ||
      this.currentState.status === 'requires_action'
        ? this.currentState.availableMethods || []
        : [];

    // If no methods available and we're still initializing, return empty array
    // The skeleton will show during initialization
    if (availableMethods.length === 0) {
      return [];
    }

    // Filter by exclusions and currency
    return availableMethods
      .filter((method) => {
        // Apply user exclusions
        if (this.excludePaymentMethods?.includes(method.id)) {
          return false;
        }
        return true;
      })
      .map((method) => {
        // Check currency validation for PayNow
        if (method.id === PAYMENT_METHODS.PAYNOW) {
          const transactionData = this.getTransactionData();
          const currency = transactionData.currency;
          if (currency !== 'SGD') {
            return {
              id: method.id,
              enabled: false,
              disabledReason: 'PayNow is only available for SGD currency',
            };
          }
        }

        return {
          id: method.id,
          enabled: true,
        };
      });
  }

  private initializeSDK(): void {
    // Validate required configuration props
    if (!this.config || !this.adapters || !this.amount || !this.currency || !this.orderId) {
      console.error(
        'OnePayment: Missing required props. Ensure config, adapters, amount, currency, and orderId are provided.',
      );
      return;
    }

    // Validate required customer information props
    if (!this.firstName || typeof this.firstName !== 'string' || this.firstName.trim() === '') {
      console.error(
        'OnePayment: firstName is required and must be a non-empty string. Customer information is required for all payment methods.',
      );
      return;
    }

    if (!this.lastName || typeof this.lastName !== 'string' || this.lastName.trim() === '') {
      console.error(
        'OnePayment: lastName is required and must be a non-empty string. Customer information is required for all payment methods.',
      );
      return;
    }

    if (!this.email || typeof this.email !== 'string' || this.email.trim() === '') {
      console.error(
        'OnePayment: email is required and must be a non-empty string. Customer information is required for all payment methods.',
      );
      return;
    }

    // Prevent re-initialization if already initialized
    if (this.isInitialized) {
      return;
    }

    // Set flag IMMEDIATELY to prevent duplicate initialization
    // (similar to React useRef pattern from original implementation)
    this.isInitialized = true;

    this.sdk?.destroy();

    this.sdk = new PaymentSDK(this.config, this.adapters);

    this.sdk.subscribe((state: State) => {
      // Detect when state changes FROM requires_action to something else
      // This happens when get-payment-data returns after 3DS form submission
      if (
        this.previousState?.status === 'requires_action' &&
        state.status !== 'requires_action' &&
        this.show3DSModal
      ) {
        this.show3DSModal = false;
        this.nextActionUrl = null;
        this.leaveEventsCounter = 0;
        // Resume polling to continue checking for final payment status
        if (this.sdk) {
          this.sdk.resumePolling();
        }
      }

      this.previousState = state;
      this.currentState = state;
      this.dispatchEvent(
        new CustomEvent('state-change', {
          detail: { state },
          bubbles: true,
          composed: true,
        }),
      );
    });

    this.sdk.on('payment_succeeded', (event) => {
      this.dispatchEvent(
        new CustomEvent('payment-success', {
          detail: { paymentIntentId: event.payload.paymentIntent.id },
          bubbles: true,
          composed: true,
        }),
      );
    });

    this.sdk.on('payment_failed', (event) => {
      this.dispatchEvent(
        new CustomEvent('payment-error', {
          detail: { error: event.payload.error.message },
          bubbles: true,
          composed: true,
        }),
      );
    });

    this.sdk.on('initialized', (event) => {
      // Store payment data for TransactionDetails
      this.paymentData = event.payload.paymentData;
      // Hide skeleton loading, show actual UI
      this.isInitializing = false;
    });

    // Handle 3DS authentication requirement
    this.sdk.on('payment_requires_action', (event) => {
      this.nextActionUrl = event.payload.actionUrl;
      this.show3DSModal = true;
      this.paymentLocked = true;
      // Reset counter for each new 3DS flow
      this.leaveEventsCounter = 0;
    });

    // Handle poll started - close 3DS modal when getPaymentData polling starts
    // This is critical for React Native where postMessage doesn't work from WebView iframe
    // BUT: Only do this for React Native, NOT for web browsers (React, Vue, Angular)
    this.sdk.on('poll_started', () => {
      // Check if we're in React Native environment
      // React Native has window.ReactNativeWebView injected by the WebView component
      const isReactNative = typeof window !== 'undefined' && 'ReactNativeWebView' in window;

      // ONLY close modal automatically for React Native (not web browsers)
      // In web browsers, modal should stay open until postMessage or state change closes it
      if (this.show3DSModal && isReactNative) {
        this.show3DSModal = false;
        this.nextActionUrl = null;
        this.leaveEventsCounter = 0;
        // MUST call resumePolling to continue interval polling for final status
        if (this.sdk) {
          this.sdk.resumePolling();
        }
      }
    });

    // Handle QR code received
    this.sdk.on(
      'qr_code_received',
      async (event: { payload: { qrCode: string; methodId: string } }) => {
        this.qrCode = event.payload.qrCode;

        if (!this.qrCode) {
          return;
        }

        // Check if qrCode is a raw QR string (EMV format) or already an image URL
        const isUrl = this.qrCode.startsWith('http://') || this.qrCode.startsWith('https://');
        const isDataUrl = this.qrCode.startsWith('data:image');

        if (!isUrl && !isDataUrl) {
          // Generate QR code image from raw string
          try {
            const dataUrl = await QRCode.toDataURL(this.qrCode, {
              width: 300,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#FFFFFF',
              },
            });
            this.qrCodeDataUrl = dataUrl;
          } catch (error) {
            this.qrCodeDataUrl = null;
          }
        } else {
          // Use the URL/data URL directly
          this.qrCodeDataUrl = this.qrCode;
        }

        this.showQRModal = true;
        this.paymentLocked = true;

        // Start 10-second auto-resume timer
        this.qrAutoResumeTimer = window.setTimeout(() => {
          this.handleQRScanned();
        }, 10000);
      },
    );

    this.sdk.initialize({
      amount: this.amount,
      currency: this.currency,
      orderId: this.orderId,
    });
  }

  private handleMethodSelect(method: PaymentMethodId): void {
    // Don't allow method switching if payment is locked
    if (this.paymentLocked) {
      return;
    }
    this.selectedMethod = method;
  }

  private handleQRScanned(): void {
    // Clear the auto-resume timer
    if (this.qrAutoResumeTimer !== null) {
      clearTimeout(this.qrAutoResumeTimer);
      this.qrAutoResumeTimer = null;
    }

    // Set polling in progress state to show loading spinner
    this.qrPollingInProgress = true;

    // DO NOT close the QR - keep it visible
    // Just resume polling to check for final payment status
    if (this.sdk) {
      this.sdk.resumePolling();
    }
  }

  private handle3DSComplete(): void {
    this.show3DSModal = false;
    this.nextActionUrl = null;
    this.leaveEventsCounter = 0; // Reset counter after closing
    // Resume polling to check for final payment status after 3DS
    if (this.sdk) {
      this.sdk.resumePolling();
    }
  }

  private handleInputChange(field: keyof CardFormData, value: string): void {
    this.cardFormData = { ...this.cardFormData, [field]: value };

    // Clear errors for this field when user starts typing
    // This provides immediate feedback when they correct the issue
    if (this.formErrors[field]) {
      const newErrors = { ...this.formErrors };
      delete newErrors[field];
      this.formErrors = newErrors;
    }
  }

  // Luhn algorithm check for card number validation
  private luhnCheck(cardNumber: string): boolean {
    // Remove all spaces
    const digits = cardNumber.replace(/\s/g, '');

    // Check if only digits
    if (!/^\d+$/.test(digits)) {
      return false;
    }

    let sum = 0;
    let isEven = false;

    // Loop through values starting from the rightmost digit
    for (let i = digits.length - 1; i >= 0; i--) {
      const char = digits.charAt(i);
      if (!char) continue;

      let digit = parseInt(char, 10);

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  }

  // Validate expiry date (MM/YY format)
  private validateExpiry(expiry: string): { valid: boolean; error?: string } {
    // Trim whitespace before validation
    const trimmedExpiry = expiry.trim();

    // Check format MM/YY - require 1-2 digits for month and exactly 2 digits for year
    // Cleave.js outputs exactly 2 digits for year with the 'y' pattern
    const formatRegex = /^\d{1,2}\/\d{2}$/;
    const formatMatches = formatRegex.test(trimmedExpiry);

    if (!formatMatches) {
      return { valid: false, error: 'Invalid format (MM/YY)' };
    }

    const parts = trimmedExpiry.split('/');
    const monthStr = parts[0];
    const yearStr = parts[1];

    if (!monthStr || !yearStr) {
      return { valid: false, error: 'Invalid format (MM/YY)' };
    }

    const month = parseInt(monthStr, 10);

    // Validate month (01-12) FIRST before year parsing
    if (month < 1 || month > 12) {
      return { valid: false, error: 'Invalid month (01-12)' };
    }

    // Get current date first
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // getMonth() is 0-indexed (0=Jan, 11=Dec)

    // Handle 2-digit year with sliding window approach for credit card expiry dates
    // This ensures proper interpretation of 2-digit years relative to the current year
    let year: number;
    if (yearStr.length === 2) {
      const inputYearTwoDigit = parseInt(yearStr, 10);
      const currentYearTwoDigit = currentYear % 100; // Get last 2 digits of current year
      const currentCentury = Math.floor(currentYear / 100) * 100; // e.g., 2000

      // Sliding window logic:
      // If the input year is significantly less than the current year (more than 80 years back),
      // assume it's meant for the next century. Otherwise, use the current century.
      // Example in 2025: "24" is 2024 (1 year back), "99" is 2099 (74 years forward)
      // This handles the year 2000-2099 range properly
      if (inputYearTwoDigit < currentYearTwoDigit) {
        // Check if it's a reasonable past date (within 5 years) or should be next century
        const yearsDiff = currentYearTwoDigit - inputYearTwoDigit;
        if (yearsDiff > 80) {
          // More than 80 years back means it's probably meant for next century
          // e.g., in 2025, "99" (25-99 = -74, but wraps) should be 2099, not 1999
          year = currentCentury + 100 + inputYearTwoDigit;
        } else {
          // Within 80 years back, use current century
          year = currentCentury + inputYearTwoDigit;
        }
      } else {
        // Input year >= current year's last 2 digits, use current century
        year = currentCentury + inputYearTwoDigit;
      }
    } else {
      // Should not reach here due to regex, but handle gracefully
      return { valid: false, error: 'Invalid year format' };
    }

    // Card is expired ONLY if:
    // 1. Year is LESS than current year (e.g., 2023 < 2024)
    // 2. OR year EQUALS current year AND month is LESS than current month
    // Note: Cards are valid through the last day of the expiry month
    // Examples:
    // - 11/2025 in November 2025: NOT expired (valid through Nov 30)
    // - 10/2025 in November 2025: expired (October already passed)
    // - 12/2034 in November 2025: NOT expired (future year)
    // - 12/2024 in November 2025: expired (year in the past)
    if (year < currentYear) {
      return { valid: false, error: 'Card has expired' };
    }

    if (year === currentYear && month < currentMonth) {
      return { valid: false, error: 'Card has expired' };
    }

    return { valid: true };
  }

  private validateCardForm(): boolean {
    const errors: Partial<Record<keyof CardFormData, string>> = {};

    // Card number validation
    if (!this.cardFormData.cardNumber.trim()) {
      errors.cardNumber = 'Please enter card number';
    } else if (!this.luhnCheck(this.cardFormData.cardNumber)) {
      errors.cardNumber = 'Invalid card number';
    }

    // Expiry date validation
    if (!this.cardFormData.expiry.trim()) {
      errors.expiry = 'Please enter expiry date';
    } else {
      const expiryValidation = this.validateExpiry(this.cardFormData.expiry);
      if (!expiryValidation.valid) {
        errors.expiry = expiryValidation.error!;
      }
    }

    // CVV validation
    if (!this.cardFormData.cvv.trim()) {
      errors.cvv = 'Please enter CVV';
    }

    // Cardholder name validation
    if (!this.cardFormData.cardholderName.trim()) {
      errors.cardholderName = 'Please enter cardholder name';
    }

    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  private handleCardPayment(): void {
    if (!this.sdk || this.currentState.status !== 'ready') {
      return;
    }

    if (!this.validateCardForm()) {
      return;
    }

    // Lock payment methods to prevent switching
    this.paymentLocked = true;

    // Parse expiry MM/YY into separate fields
    const expiryParts = this.cardFormData.expiry.split('/');
    const expiryMonth = (expiryParts[0] || '').trim();
    const expiryYearShort = (expiryParts[1] || '').trim().substring(0, 2); // Take only first 2 digits
    const expiryYear = `20${expiryYearShort}`; // Convert to 4-digit year

    this.sdk.submitPayment(PAYMENT_METHODS.CARD, {
      cardNumber: this.cardFormData.cardNumber.replace(/\s/g, ''),
      expiryMonth,
      expiryYear,
      cvv: this.cardFormData.cvv,
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
    });
  }

  private handlePayNowPayment(): void {
    if (!this.sdk || this.currentState.status !== 'ready') {
      return;
    }

    // Lock payment methods to prevent switching
    this.paymentLocked = true;

    // Use customer info from props
    this.sdk.submitPayment(PAYMENT_METHODS.PAYNOW, {
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
    });

    // showQRCode will be set by the event listener when qrCode is received
  }

  // --- Render Methods ---
  private getTransactionData() {
    const data = this.paymentData;

    // Return fallback data if payment data is not loaded yet
    if (!data) {
      return {
        currency: this.currency,
        charge: this.amount,
        fees: 0,
        feeBreakdown: {
          transactionFee: 0,
          merchantFee: 0,
          merchantFeeLabel: '',
        },
        netAmount: this.amount,
      };
    }

    // Find merchant fee configuration
    const merchantFeeConfig = data.fees?.find((fee: FeeConfig) => fee.owner === 'merchant');

    // Get merchant fee label (percentage or fixed amount)
    const getMerchantFeeLabel = (): string => {
      if (!merchantFeeConfig) return '';

      if (merchantFeeConfig.type === 'percentage') {
        return `${merchantFeeConfig.value}%`;
      }

      return `${merchantFeeConfig.value} ${merchantFeeConfig.currency}`;
    };

    // Convert to numbers for calculations
    const totalFeeNum =
      typeof data.totalFee === 'number' ? data.totalFee : Number(data.totalFee) || 0;

    const totalSystemFeeNum =
      typeof data.totalSystemFee === 'number'
        ? data.totalSystemFee
        : Number(data.totalSystemFee) || 0;

    const initialAmountNum =
      data.initialAmount != null
        ? typeof data.initialAmount === 'number'
          ? data.initialAmount
          : Number(data.initialAmount) || 0
        : this.amount;

    // Get customerAmount (this is what customer actually pays)
    const customerAmountNum =
      data.customerAmount != null
        ? typeof data.customerAmount === 'number'
          ? data.customerAmount
          : Number(data.customerAmount) || 0
        : initialAmountNum;

    // Merchant fee = totalFee - totalSystemFee
    const merchantFeeNum = totalFeeNum - totalSystemFeeNum;

    return {
      currency: data.currency || this.currency,
      charge: initialAmountNum,
      fees: totalFeeNum,
      feeBreakdown: {
        transactionFee: merchantFeeNum,
        merchantFee: merchantFeeNum,
        merchantFeeLabel: getMerchantFeeLabel() || '0%',
      },
      netAmount: customerAmountNum,
    };
  }

  private renderTransactionDetailsSkeleton() {
    return html`
      <div class="skeleton-transaction">
        <div class="skeleton skeleton-title"></div>
        <div class="skeleton-row">
          <div class="skeleton skeleton-label"></div>
          <div class="skeleton skeleton-value"></div>
        </div>
        <div class="skeleton-row">
          <div class="skeleton skeleton-label"></div>
          <div class="skeleton skeleton-value"></div>
        </div>
        <div class="skeleton-row">
          <div class="skeleton skeleton-label"></div>
          <div class="skeleton skeleton-value"></div>
        </div>
        <div class="skeleton-divider"></div>
        <div class="skeleton-net-row">
          <div class="skeleton skeleton-net-label"></div>
          <div class="skeleton skeleton-net-value"></div>
        </div>
      </div>
    `;
  }

  private renderPaymentMethodsSkeleton() {
    return html`
      <div>
        <div class="skeleton-method">
          <div class="skeleton skeleton-radio"></div>
          <div class="skeleton skeleton-icon"></div>
          <div class="skeleton skeleton-method-name"></div>
        </div>
        <div class="skeleton-method">
          <div class="skeleton skeleton-radio"></div>
          <div class="skeleton skeleton-icon"></div>
          <div class="skeleton skeleton-method-name"></div>
        </div>
      </div>
    `;
  }

  private renderSkeletons() {
    // Build dynamic styles for width and maxWidth
    const elementStyles = [
      this.width ? `--one-payment-width: ${this.width}` : '',
      this.maxWidth ? `--one-payment-max-width: ${this.maxWidth}` : '',
    ]
      .filter(Boolean)
      .join('; ');

    return html`
      <div class="payment-element" style="${elementStyles}">
        ${this.renderTransactionDetailsSkeleton()}
        <div class="header">Select a payment method</div>
        ${this.renderPaymentMethodsSkeleton()}
      </div>
    `;
  }

  private renderTransactionDetails() {
    const transactionData = this.getTransactionData();
    const formatAmount = (amount: number): string => amount.toFixed(2);

    return html`
      <div class="transaction-details">
        <h2 class="transaction-title">Transaction details</h2>

        <div class="details-grid">
          <div class="details-row">
            <span class="details-label">Currency:</span>
            <span class="details-value">${transactionData.currency}</span>
          </div>

          <div class="details-row">
            <span class="details-label">Charge:</span>
            <span class="details-value">${formatAmount(transactionData.charge)}</span>
          </div>

          <div class="details-row">
            <button
              class="fees-toggle"
              @click=${() => (this.feesExpanded = !this.feesExpanded)}
              type="button"
              aria-label=${this.feesExpanded ? 'Collapse fees' : 'Expand fees'}
            >
              <span class="details-label"> Fees ${this.feesExpanded ? '∧' : '∨'} </span>
            </button>
            <span class="details-value">${formatAmount(transactionData.fees)}</span>
          </div>

          <div
            class="fee-breakdown"
            style=${`
              max-height: ${this.feesExpanded ? '200px' : '0'};
              opacity: ${this.feesExpanded ? 1 : 0};
              margin-top: ${this.feesExpanded ? '0.625rem' : '0'};
            `}
          >
            ${transactionData.feeBreakdown
              ? html`
                  <div class="fee-breakdown-inner">
                    <div class="breakdown-row">
                      <span class="breakdown-label">Transaction fee:</span>
                      <span class="breakdown-value">
                        ${formatAmount(transactionData.feeBreakdown.transactionFee)}
                      </span>
                    </div>
                    <div class="breakdown-row">
                      <span class="breakdown-label">
                        Merchant
                        fee${transactionData.feeBreakdown.merchantFeeLabel
                          ? ` (${transactionData.feeBreakdown.merchantFeeLabel})`
                          : ''}:
                      </span>
                      <span class="breakdown-value">
                        ${formatAmount(transactionData.feeBreakdown.merchantFee)}
                      </span>
                    </div>
                  </div>
                `
              : ''}
          </div>
        </div>

        <div class="transaction-divider"></div>

        <div class="net-amount-row">
          <span class="net-label">Net amount:</span>
          <span class="net-value">${formatAmount(transactionData.netAmount)}</span>
        </div>
      </div>
    `;
  }

  private renderCardForm() {
    // Setup Cleave.js on first render
    setTimeout(() => this.setupCleave(), 0);

    const cvvMaxLength = this.cardBrand === 'amex' ? 4 : 3;
    const cvvPlaceholder = this.cardBrand === 'amex' ? '1234' : '123';

    return html`
      <div class="card-form">
        <h4 class="form-title">Card Information</h4>
        <div class="form-fields">
          <!-- Card Number -->
          <div class="form-group">
            <label class="form-label">Card Number</label>
            <input
              id="cardNumber"
              type="text"
              class="input ${this.formErrors.cardNumber ? 'input-error' : ''}"
              placeholder="1234 1234 1234 1234"
              .value=${this.cardFormData.cardNumber}
              @input=${(e: Event) =>
                this.handleInputChange('cardNumber', (e.target as HTMLInputElement).value)}
            />
            ${this.formErrors.cardNumber
              ? html`<div class="error-message">${this.formErrors.cardNumber}</div>`
              : ''}
          </div>

          <!-- Single Expiry Field -->
          <div class="form-group">
            <label class="form-label">Expiry Date</label>
            <input
              id="expiry"
              type="text"
              class="input ${this.formErrors.expiry ? 'input-error' : ''}"
              placeholder="MM/YY"
              .value=${this.cardFormData.expiry}
              @input=${(e: Event) => {
                // Get value after Cleave has processed it
                const input = e.target as HTMLInputElement;
                // Use setTimeout to ensure Cleave formatting is complete
                setTimeout(() => {
                  this.handleInputChange('expiry', input.value);
                }, 0);
              }}
            />
            ${this.formErrors.expiry
              ? html`<div class="error-message">${this.formErrors.expiry}</div>`
              : ''}
          </div>

          <!-- CVV -->
          <div class="form-group">
            <label class="form-label">CVV</label>
            <input
              type="text"
              class="input ${this.formErrors.cvv ? 'input-error' : ''}"
              placeholder="${cvvPlaceholder}"
              maxlength="${cvvMaxLength}"
              .value=${this.cardFormData.cvv}
              @input=${(e: Event) => {
                const value = (e.target as HTMLInputElement).value.replace(/\D/g, '');
                (e.target as HTMLInputElement).value = value;
                this.handleInputChange('cvv', value);
              }}
            />
            ${this.formErrors.cvv
              ? html`<div class="error-message">${this.formErrors.cvv}</div>`
              : ''}
          </div>

          <!-- Cardholder Name -->
          <div class="form-group">
            <label class="form-label">Name on card</label>
            <input
              type="text"
              class="input ${this.formErrors.cardholderName ? 'input-error' : ''}"
              placeholder="JOHN DOE"
              .value=${this.cardFormData.cardholderName}
              @input=${(e: Event) => {
                // Only allow letters and spaces, automatically convert to uppercase
                const value = (e.target as HTMLInputElement).value
                  .replace(/[^A-Za-z\s]/g, '')
                  .toUpperCase();
                (e.target as HTMLInputElement).value = value;
                this.handleInputChange('cardholderName', value);
              }}
            />
            ${this.formErrors.cardholderName
              ? html`<div class="error-message">${this.formErrors.cardholderName}</div>`
              : ''}
          </div>
        </div>

        <div class="submit-section">
          <button
            class="pay-button ${this.currentState.status === 'processing' ||
            this.currentState.status === 'requires_action'
              ? 'loading'
              : ''}"
            @click=${this.handleCardPayment}
            ?disabled=${this.currentState.status === 'processing' ||
            this.currentState.status === 'requires_action'}
          >
            ${this.currentState.status === 'processing' ||
            this.currentState.status === 'requires_action'
              ? 'Processing...'
              : `Pay ${this.currency} ${this.amount.toFixed(2)}`}
          </button>
        </div>
      </div>
    `;
  }

  private renderPayNowContent() {
    // If QR code is available, show it inline instead of instructions
    if (this.showQRModal && this.qrCodeDataUrl) {
      return html`
        <div class="paynow-container">
          <!-- Render generated QR code image -->
          <div class="qr-code-wrapper">
            <img src="${this.qrCodeDataUrl}" alt="QR Code" class="qr-canvas" />
          </div>

          <button
            class="scanned-button ${this.qrPollingInProgress ? 'loading' : ''}"
            @click=${() => {
              this.handleQRScanned();
            }}
            ?disabled=${this.qrPollingInProgress}
          >
            ${this.qrPollingInProgress ? 'Verifying...' : 'I Have Scanned the QR Code'}
          </button>
        </div>
      `;
    }

    // Show instructions when QR is not yet available
    return html`
      <div class="paynow-container">
        <div class="paynow-instructions">
          <h3 class="instructions-title">How to Pay by QR:</h3>

          <div class="instruction-steps">
            <div class="instruction-step">
              <div class="step-number">1</div>
              <p class="step-text"><strong>"Pay"</strong> will take you to payment details.</p>
            </div>

            <div class="instruction-step">
              <div class="step-number">2</div>
              <p class="step-text">Scan the QR code with your bank app.</p>
            </div>
          </div>

          <div class="submit-section">
            <button
              class="pay-button ${this.currentState.status === 'processing' ||
              this.currentState.status === 'requires_action'
                ? 'loading'
                : ''}"
              @click=${this.handlePayNowPayment}
              ?disabled=${this.currentState.status === 'processing' ||
              this.currentState.status === 'requires_action'}
            >
              ${this.currentState.status === 'processing' ||
              this.currentState.status === 'requires_action'
                ? 'Processing...'
                : `Pay ${this.currency} ${this.amount.toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private render3DSModal() {
    if (!this.show3DSModal || !this.nextActionUrl) {
      return null;
    }

    return html`
      <div class="modal-overlay">
        <div class="modal-content iframe-modal">
          <iframe
            class="modal-iframe"
            src="${this.nextActionUrl}"
            title="3D Secure Authentication"
            allow="payment"
          ></iframe>
        </div>
      </div>
    `;
  }

  private renderStatusBlock() {
    if (this.currentState.status === 'succeeded') {
      return html`
        <div class="result-card success">
          <div class="result-icon-wrapper">
            <div class="result-icon">✓</div>
          </div>
          <h2 class="result-title">Payment Successful</h2>
          <p class="result-message">
            Your payment of ${this.currency} ${this.amount.toFixed(2)} has been processed
            successfully.
          </p>
        </div>
      `;
    }

    if (this.currentState.status === 'failed') {
      const errorMessage = this.currentState.error
        ? this.currentState.error.message
        : 'An error occurred while processing your payment';

      return html`
        <div class="result-card error">
          <div class="result-icon-wrapper">
            <div class="result-icon">✕</div>
          </div>
          <h2 class="result-title">Payment Failed</h2>
          <p class="result-message">${errorMessage}</p>
          <button class="retry-button" @click=${() => window.location.reload()}>Try Again</button>
        </div>
      `;
    }

    return null;
  }

  // --- Main Render ---
  override render() {
    // Show skeletons during initialization (before payment data is loaded)
    if (
      (this.currentState.status === 'idle' || this.currentState.status === 'initializing') &&
      this.isInitializing &&
      !this.paymentData
    ) {
      return this.renderSkeletons();
    }

    // Determine if we should show payment methods or status block
    const isTerminalStatus =
      this.currentState.status === 'succeeded' || this.currentState.status === 'failed';
    const showPaymentMethods = !isTerminalStatus;

    // Get filtered payment methods
    const filteredMethods = this.getFilteredPaymentMethods();

    // Auto-select first available method if none selected or current selection is not available
    if (filteredMethods.length > 0) {
      const currentMethodAvailable = filteredMethods.some(
        (m) => m.id === this.selectedMethod && m.enabled,
      );
      if (!this.selectedMethod || !currentMethodAvailable) {
        const firstEnabledMethod = filteredMethods.find((m) => m.enabled);
        if (firstEnabledMethod && this.selectedMethod !== firstEnabledMethod.id) {
          this.selectedMethod = firstEnabledMethod.id;
        }
      }
    }

    // Helper function to get method display name
    const getMethodName = (id: PaymentMethodId): string => {
      return id === PAYMENT_METHODS.CARD ? 'Card' : 'PayNow';
    };

    // Helper function to render the right icon based on payment method
    const renderMethodIconRight = (id: PaymentMethodId) => {
      if (id === PAYMENT_METHODS.CARD) {
        // Card icon SVG
        return html`
          <div class="method-icon-right">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="none"
                d="M22 10H2M11 14H6M2 8.2L2 15.8C2 16.9201 2 17.4802 2.21799 17.908C2.40973 18.2843 2.71569 18.5903 3.09202 18.782C3.51984 19 4.07989 19 5.2 19L18.8 19C19.9201 19 20.4802 19 20.908 18.782C21.2843 18.5903 21.5903 18.2843 21.782 17.908C22 17.4802 22 16.9201 22 15.8V8.2C22 7.0799 22 6.51984 21.782 6.09202C21.5903 5.7157 21.2843 5.40974 20.908 5.21799C20.4802 5 19.9201 5 18.8 5L5.2 5C4.0799 5 3.51984 5 3.09202 5.21799C2.7157 5.40973 2.40973 5.71569 2.21799 6.09202C2 6.51984 2 7.07989 2 8.2Z"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        `;
      } else if (id === PAYMENT_METHODS.PAYNOW) {
        return html`
          <div class="method-icon-right">
            <img src="${PAYNOW_ICON_DATA_URL}" alt="PayNow" />
          </div>
        `;
      }
      return null;
    };

    // Build dynamic styles for width and maxWidth
    const elementStyles = [
      this.width ? `--one-payment-width: ${this.width}` : '',
      this.maxWidth ? `--one-payment-max-width: ${this.maxWidth}` : '',
    ]
      .filter(Boolean)
      .join('; ');

    return html`
      <div class="payment-element" style="${elementStyles}">
        ${this.renderTransactionDetails()}
        ${showPaymentMethods
          ? html`
              <div class="header">Select a payment method</div>
              <div class="payment-methods-list">
                ${filteredMethods.map((method) => {
                  const isSelected = this.selectedMethod === method.id;
                  const isDisabled = !method.enabled || (this.paymentLocked && !isSelected);

                  return html`
                    <div
                      class="payment-method-item"
                      ${method.disabledReason ? html`data-tooltip="${method.disabledReason}"` : ''}
                    >
                      <div
                        class="method-header ${isSelected ? 'selected' : ''} ${isDisabled
                          ? 'disabled'
                          : ''}"
                        @click=${() => method.enabled && this.handleMethodSelect(method.id)}
                      >
                        <input
                          type="radio"
                          class="radio"
                          name="payment-method"
                          .checked=${isSelected}
                          ?disabled=${isDisabled}
                          @click=${(e: Event) => e.stopPropagation()}
                        />
                        <span class="method-name">${getMethodName(method.id)}</span>
                        ${renderMethodIconRight(method.id)}
                      </div>
                      ${isSelected
                        ? html`<div class="method-content">
                            ${method.id === PAYMENT_METHODS.CARD
                              ? this.renderCardForm()
                              : this.renderPayNowContent()}
                          </div>`
                        : ''}
                    </div>
                  `;
                })}
              </div>
            `
          : ''}
        ${isTerminalStatus ? this.renderStatusBlock() : ''}
      </div>

      ${this.render3DSModal()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'one-payment': OnePayment;
  }
}
